import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { 
  Building,
  DollarSign,
  BarChart3,
  Settings,
  Shield,
  Truck,
  Factory,
  FileText,
  Calendar,
  MessageSquare,
  Target,
  TrendingUp,
  Database,
  Cog,
  Clock,
  Globe,
  UserCheck,
  BookOpen,
  Heart,
  Award,
  Zap,
  CheckCircle,
  Layers,
  PieChart,
  Users,
  Code,
  Package,
  Workflow,
  Lock,
  Warehouse,
  Wrench,
  Calculator,
  Cloud,
  ShoppingCart,
  Boxes,
  Navigation,
  Smartphone,
  Grid,
  Link,
  Cpu,
  Binary,
  Server,
  Activity
} from "lucide-react";

interface SAPModule {
  icon: any;
  title: string;
  category: string;
  description: string;
  features: string[];
  technologies: string[];
  href: string;
}

interface SAPMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const SAPMenu = ({ isOpen, onClose }: SAPMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Group modules by category for better organization
  const moduleCategories = {
    "Finance & Controlling": [
      {
        icon: DollarSign,
        title: "SAP FI",
        category: "Finance",
        description: "Complete financial accounting solution with general ledger, accounts payable/receivable, and asset accounting.",
        features: ["General Ledger Management", "Accounts Payable/Receivable", "Asset Accounting", "Bank Accounting"],
        technologies: ["SAP FI", "SAP New GL", "SAP Asset Accounting", "SAP Bank Communication Management"],
        href: "/sap/fi"
      },
      {
        icon: PieChart,
        title: "SAP CO",
        category: "Finance",
        description: "Cost center accounting, profit center analysis, and internal orders for comprehensive controlling.",
        features: ["Cost Center Accounting", "Profit Center Analysis", "Internal Orders", "Product Costing"],
        technologies: ["SAP CO", "SAP Profitability Analysis", "SAP Product Cost Controlling"],
        href: "/sap/co"
      },
      {
        icon: Calculator,
        title: "SAP FICO",
        category: "Finance",
        description: "Integrated finance and controlling solution combining financial accounting with management accounting.",
        features: ["Financial Accounting", "Management Accounting", "Cost Center Management", "Profitability Analysis"],
        technologies: ["SAP FICO", "SAP New GL", "SAP Controlling", "SAP Profitability Analysis"],
        href: "/sap/fico"
      }
    ],
    "Manufacturing & Operations": [
      {
        icon: Factory,
        title: "SAP PP",
        category: "Manufacturing",
        description: "Manufacturing execution, capacity planning, and shop floor control for production optimization.",
        features: ["Production Planning", "Capacity Planning", "Shop Floor Control", "Manufacturing Execution"],
        technologies: ["SAP PP", "SAP Manufacturing Execution", "SAP Capacity Planning"],
        href: "/sap/pp"
      },
      {
        icon: Cpu,
        title: "SAP Digital Manufacturing",
        category: "Manufacturing",
        description: "Digital manufacturing solutions with IoT integration, real-time monitoring, and smart factory capabilities.",
        features: ["IoT Integration", "Real-time Monitoring", "Smart Factory", "Digital Twin", "Predictive Maintenance"],
        technologies: ["SAP Digital Manufacturing", "SAP Manufacturing Execution", "SAP IoT", "SAP Edge Services"],
        href: "/sap/digital-manufacturing"
      },
      {
        icon: Wrench,
        title: "SAP PPQM",
        category: "Manufacturing",
        description: "Integrated production planning with quality management for manufacturing excellence.",
        features: ["Production Planning", "Quality Planning", "Inspection Management", "Manufacturing Analytics"],
        technologies: ["SAP PP", "SAP QM", "SAP Manufacturing Execution", "SAP Quality Management"],
        href: "/sap/ppqm"
      },
      {
        icon: Award,
        title: "SAP QM",
        category: "Quality",
        description: "Quality planning, inspection, and certificate management for quality assurance.",
        features: ["Quality Planning", "Inspection Management", "Certificate Management", "Quality Analytics"],
        technologies: ["SAP QM", "SAP Quality Management in EHS"],
        href: "/sap/qm"
      },
      {
        icon: Package,
        title: "SAP PLM",
        category: "Manufacturing",
        description: "Complete product lifecycle management from ideation to retirement with collaboration tools.",
        features: ["Product Development", "Document Management", "Change Management", "Recipe Management"],
        technologies: ["SAP PLM", "SAP Product Development", "SAP Document Management"],
        href: "/sap/plm"
      }
    ],
    "Supply Chain & Logistics": [
      {
        icon: BarChart3,
        title: "SAP MM",
        category: "Operations",
        description: "Procurement, inventory management, and vendor evaluation for materials management.",
        features: ["Procurement Processing", "Inventory Management", "Vendor Evaluation", "Purchase Requisitions"],
        technologies: ["SAP MM", "SAP SRM", "SAP Ariba", "SAP Invoice Management"],
        href: "/sap/mm"
      },
      {
        icon: Truck,
        title: "SAP SD",
        category: "Sales",
        description: "Order management, pricing, shipping, and billing for comprehensive sales operations.",
        features: ["Order Management", "Pricing Configuration", "Shipping Processing", "Billing & Invoicing"],
        technologies: ["SAP SD", "SAP Variant Configuration", "SAP Transportation Management"],
        href: "/sap/sd"
      },
      {
        icon: Warehouse,
        title: "SAP EWM",
        category: "Logistics",
        description: "Advanced warehouse management with automated processes, labor management, and inventory optimization.",
        features: ["Warehouse Automation", "Labor Management", "Inventory Optimization", "Cross-docking", "Wave Management"],
        technologies: ["SAP EWM", "SAP Warehouse Management", "SAP Transportation Management", "SAP Yard Management"],
        href: "/sap/ewm"
      },
      {
        icon: Navigation,
        title: "SAP TM",
        category: "Logistics",
        description: "Transportation planning, execution, and freight management for logistics optimization.",
        features: ["Transportation Planning", "Freight Management", "Carrier Management", "Route Optimization"],
        technologies: ["SAP TM", "SAP Transportation Management", "SAP Event Management"],
        href: "/sap/tm"
      },
      {
        icon: Target,
        title: "SAP SRM",
        category: "Procurement",
        description: "Vendor management, sourcing, and contract management for supplier relationships.",
        features: ["Vendor Management", "Strategic Sourcing", "Contract Management", "Supplier Evaluation"],
        technologies: ["SAP SRM", "SAP Ariba", "SAP Fieldglass"],
        href: "/sap/srm"
      }
    ],
    "Human Resources": [
      {
        icon: Users,
        title: "SAP HR/HCM",
        category: "HR",
        description: "Personnel administration, payroll, and talent management for human resources.",
        features: ["Personnel Administration", "Payroll Processing", "Talent Management", "Time Management"],
        technologies: ["SAP HCM", "SAP SuccessFactors", "SAP Time Management"],
        href: "/sap/hr"
      },
      {
        icon: UserCheck,
        title: "SAP SuccessFactors",
        category: "HR",
        description: "Employee Central cloud solution for core HR processes, organizational management, and employee data.",
        features: ["Employee Data Management", "Organizational Management", "HR Analytics", "Employee Self-Service"],
        technologies: ["SAP SuccessFactors EC", "SAP Employee Central", "SAP SuccessFactors HXM", "SAP HR Analytics"],
        href: "/sap/successfactors-ec"
      },
      {
        icon: Clock,
        title: "SAP Time Management",
        category: "HR",
        description: "Time tracking, attendance, and shift planning for workforce management.",
        features: ["Time Tracking", "Attendance Management", "Shift Planning", "Leave Management"],
        technologies: ["SAP Time Management", "SAP SuccessFactors Time Tracking"],
        href: "/sap/time"
      }
    ],
    "Customer & Service Management": [
      {
        icon: Globe,
        title: "SAP CRM",
        category: "CRM",
        description: "Sales force automation, marketing campaigns, and service management for customer relations.",
        features: ["Sales Force Automation", "Marketing Campaigns", "Service Management", "Customer Analytics"],
        technologies: ["SAP CRM", "SAP Marketing Cloud", "SAP Service Cloud"],
        href: "/sap/crm"
      },
      {
        icon: Cloud,
        title: "SAP C4C",
        category: "CRM",
        description: "Cloud-based customer relationship management with sales, service, and marketing capabilities.",
        features: ["Sales Automation", "Service Management", "Marketing Campaigns", "Customer Analytics"],
        technologies: ["SAP C4C", "SAP Cloud for Customer", "SAP Marketing Cloud", "SAP Service Cloud"],
        href: "/sap/c4c"
      },
      {
        icon: Activity,
        title: "SAP Service Cloud V2",
        category: "Service",
        description: "Next-generation service cloud platform with AI-powered customer service and field service management.",
        features: ["AI-Powered Service", "Omnichannel Support", "Knowledge Management", "Service Analytics"],
        technologies: ["SAP Service Cloud V2", "SAP Conversational AI", "SAP Knowledge Central", "SAP Service Analytics"],
        href: "/sap/service-cloud-v2"
      },
      {
        icon: Navigation,
        title: "SAP FSM",
        category: "Service",
        description: "Mobile field service management with scheduling, dispatching, and technician enablement.",
        features: ["Mobile Service", "Scheduling & Dispatching", "Work Order Management", "Technician Tools"],
        technologies: ["SAP FSM", "SAP Field Service Management", "SAP Mobile Services", "SAP Work Manager"],
        href: "/sap/fsm"
      },
      {
        icon: Server,
        title: "SAP ECC CS",
        category: "Customer Service",
        description: "Customer service management with incident tracking, service level agreements, and customer support workflows.",
        features: ["Incident Management", "Service Level Agreements", "Customer Support Workflows", "Service Analytics"],
        technologies: ["SAP ECC CS", "SAP Service Management", "SAP Customer Engagement"],
        href: "/sap/ecc-cs"
      }
    ],
    "Analytics & Business Intelligence": [
      {
        icon: Database,
        title: "SAP BW",
        category: "Analytics",
        description: "Data warehousing, reporting, and business intelligence for data-driven decisions.",
        features: ["Data Warehousing", "ETL Processing", "Report Generation", "Analytics Dashboards"],
        technologies: ["SAP BW/4HANA", "SAP Analytics Cloud", "SAP Data Services"],
        href: "/sap/bw"
      },
      {
        icon: Binary,
        title: "SAP Analytics Cloud",
        category: "Analytics",
        description: "Comprehensive analytics platform with business intelligence, planning, and predictive analytics.",
        features: ["Business Intelligence", "Planning & Budgeting", "Predictive Analytics", "Data Visualization"],
        technologies: ["SAP Analytics Cloud", "SAP SAC", "SAP Predictive Analytics", "SAP Data Intelligence"],
        href: "/sap/sac"
      },
      {
        icon: TrendingUp,
        title: "SAP Business Intelligence",
        category: "Analytics",
        description: "Analytics, dashboards, and performance management for business intelligence.",
        features: ["Business Analytics", "Performance Dashboards", "KPI Management", "Predictive Analytics"],
        technologies: ["SAP BusinessObjects", "SAP Analytics Cloud", "SAP Lumira"],
        href: "/sap/bi"
      }
    ],
    "Technical & Integration": [
      {
        icon: Settings,
        title: "SAP BASIS",
        category: "Technical",
        description: "System administration, performance tuning, and security for SAP infrastructure.",
        features: ["System Administration", "Performance Tuning", "Database Management", "Transport Management"],
        technologies: ["SAP BASIS", "SAP Solution Manager", "SAP HANA Administration"],
        href: "/sap/basis"
      },
      {
        icon: Server,
        title: "SAP Migration",
        category: "Migration",
        description: "Comprehensive SAP migration services including OS/DB migration, cloud migration, ECC to S/4HANA transformation, and database migration to HANA.",
        features: ["OS/DB Migration", "Cloud Migration", "S/4HANA Transformation", "Database Migration to HANA", "Unicode Migration"],
        technologies: ["SAP SWPM", "Migration Monitor", "DMO", "SUM Tool", "LTMC", "SAP Data Services"],
        href: "/sap/migration"
      },
      {
        icon: Link,
        title: "SAP CPI",
        category: "Integration",
        description: "Cloud-based integration platform for connecting SAP and non-SAP applications with pre-built connectors.",
        features: ["Cloud Integration", "Pre-built Connectors", "API Management", "Message Processing"],
        technologies: ["SAP CPI", "SAP Cloud Platform", "SAP API Management", "SAP Integration Suite"],
        href: "/sap/cpi"
      },
      {
        icon: Layers,
        title: "SAP NetWeaver",
        category: "Integration",
        description: "Integration platform, portal technology, and master data management for SAP landscapes.",
        features: ["Integration Platform", "Portal Technology", "Master Data Management", "Web Services"],
        technologies: ["SAP NetWeaver", "SAP Process Integration", "SAP Master Data Management"],
        href: "/sap/netweaver"
      },
      {
        icon: Code,
        title: "SAP ABAP",
        category: "Development",
        description: "Advanced Business Application Programming language for SAP development and customization.",
        features: ["Custom Development", "Enhancement Framework", "Workflow Development", "Report Programming"],
        technologies: ["SAP ABAP", "SAP ABAP on HANA", "SAP ABAP Development Tools", "SAP Enhancement Framework"],
        href: "/sap/abap"
      }
    ],
    "Security & Authorization": [
      {
        icon: Shield,
        title: "SAP Security",
        category: "Security",
        description: "User management, authorization, and audit compliance for SAP security.",
        features: ["User Management", "Role-based Authorization", "Audit Compliance", "GRC Implementation"],
        technologies: ["SAP GRC", "SAP Identity Management", "SAP Authorization"],
        href: "/sap/security"
      },
      {
        icon: Lock,
        title: "SAP Authorization",
        category: "Security",
        description: "Comprehensive authorization management with role design, user provisioning, and access control.",
        features: ["Role Design", "User Provisioning", "Access Control", "Authorization Objects", "Segregation of Duties"],
        technologies: ["SAP Authorization", "SAP GRC Access Control", "SAP Identity Management", "SAP Role Management"],
        href: "/sap/authorization"
      }
    ],
    "Digital Commerce & Cloud": [
      {
        icon: ShoppingCart,
        title: "SAP Commerce",
        category: "eCommerce",
        description: "Enterprise e-commerce platform with omnichannel capabilities and B2B/B2C support.",
        features: ["Omnichannel Commerce", "B2B & B2C Support", "Product Content Management", "Order Management"],
        technologies: ["SAP Commerce Cloud", "SAP Hybris", "SAP Customer Data Cloud", "SAP Commerce APIs"],
        href: "/sap/commerce"
      },
      {
        icon: Smartphone,
        title: "SAP Spartacus",
        category: "Frontend",
        description: "Modern JavaScript storefront framework for SAP Commerce Cloud with headless architecture.",
        features: ["Headless Commerce", "Angular Framework", "Progressive Web App", "Mobile Responsive"],
        technologies: ["SAP Spartacus", "Angular", "TypeScript", "Progressive Web Apps", "SAP Commerce APIs"],
        href: "/sap/spartacus"
      },
      {
        icon: Grid,
        title: "SAP Flexframe",
        category: "Framework",
        description: "Flexible development framework for building custom SAP applications with modern web technologies.",
        features: ["Custom App Development", "Modern UI Framework", "API Integration", "Responsive Design"],
        technologies: ["SAP Flexframe", "Web Components", "REST APIs", "Modern JavaScript"],
        href: "/sap/flexframe"
      }
    ],
    "Industry-Specific & Specialized": [
      {
        icon: Building,
        title: "SAP Real Estate",
        category: "Real Estate",
        description: "Property management, lease administration, and space management for real estate operations.",
        features: ["Property Management", "Lease Administration", "Space Management", "Facility Management"],
        technologies: ["SAP Real Estate Management", "SAP Facility Management"],
        href: "/sap/real-estate"
      },
      {
        icon: Heart,
        title: "SAP Healthcare",
        category: "Healthcare",
        description: "Patient management, medical billing, and clinical workflows for healthcare industry.",
        features: ["Patient Management", "Medical Billing", "Clinical Workflows", "Healthcare Analytics"],
        technologies: ["SAP for Healthcare", "SAP Medical Research Insights"],
        href: "/sap/healthcare"
      },
      {
        icon: BookOpen,
        title: "SAP Education",
        category: "Education",
        description: "Student information systems and academic planning for educational institutions.",
        features: ["Student Information Systems", "Academic Planning", "Course Management", "Student Analytics"],
        technologies: ["SAP for Higher Education", "SAP Student Lifecycle Management"],
        href: "/sap/education"
      },
      {
        icon: Zap,
        title: "SAP Energy & Utilities",
        category: "Industry",
        description: "Meter reading, billing, and customer service for energy and utilities sector.",
        features: ["Meter Reading", "Utility Billing", "Customer Service", "Energy Trading"],
        technologies: ["SAP IS-U", "SAP Energy Data Management"],
        href: "/sap/utilities"
      }
    ],
    "Process & Workflow Management": [
      {
        icon: Cog,
        title: "SAP Workflow",
        category: "Process",
        description: "Business process automation and approval workflows for streamlined operations.",
        features: ["Process Automation", "Approval Workflows", "Task Management", "Event Processing"],
        technologies: ["SAP Workflow", "SAP Business Workflow", "SAP Process Integration"],
        href: "/sap/workflow"
      },
      {
        icon: FileText,
        title: "SAP Document Management",
        category: "Document",
        description: "Document storage, workflow, and archiving solutions for document management.",
        features: ["Document Storage", "Workflow Automation", "Archive Management", "Content Repository"],
        technologies: ["SAP DMS", "SAP Content Server", "SAP Archive Development Kit"],
        href: "/sap/dms"
      },
      {
        icon: MessageSquare,
        title: "SAP Knowledge Management",
        category: "Knowledge",
        description: "Content management, collaboration, and expertise location for knowledge sharing.",
        features: ["Content Management", "Collaboration Tools", "Expertise Location", "Knowledge Base"],
        technologies: ["SAP Knowledge Management", "SAP Collaboration"],
        href: "/sap/km"
      }
    ]
  };

  const handleModuleClick = (module: SAPModule) => {
    navigate(module.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef}
      className="fixed top-16 left-0 right-0 bg-background border-b border-border shadow-xl z-[55]"
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">SAP Modules & Services</h3>
        <div className="max-h-96 overflow-y-auto space-y-6">
          {Object.entries(moduleCategories).map(([categoryName, modules]) => (
            <div key={categoryName} className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide border-b border-border pb-1">
                {categoryName}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {modules.map((module, index) => (
                  <button
                    key={index}
                    onClick={() => handleModuleClick(module)}
                    className="text-left px-3 py-2 text-sm text-foreground hover:text-orange-600 hover:bg-orange-50/50 transition-colors duration-200 rounded-md"
                  >
                    <span className="font-medium text-xs">{module.title}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SAPMenu;